import React, { useEffect, useState, useRef } from "react";
import { Plus, X, Loader2, FileQuestion, Search, Settings2, Ban } from "lucide-react";
import BreadCrumb from "Common/BreadCrumb";
import { apiFetch } from "../../api/api";
import { AxiosError } from "axios";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";


const SurveyBuilder = () => {
  const { form_id } = useParams<{ form_id: string }>();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // modal های مختلف
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);

  // مربوط به جستجو/افزودن سؤال
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  // مربوط به شرط
  const [selectedQuestion, setSelectedQuestion] = useState<any | null>(null);
  const [selectedOption, setSelectedOption] = useState<string>(""); // متن/Title فعلی که در UI استفاده میشه
  const [selectedOptionOID, setSelectedOptionOID] = useState<string | number>(""); // OID جداگانه برای ارسال به API
  const [targetQuestion, setTargetQuestion] = useState<string>("");

  // لیست شرط‌ها
  const [conditions, setConditions] = useState<any[]>([]);

  // متون خوش‌آمد / پایان
  const [welcomeText, setWelcomeText] = useState("");
  const [endText, setEndText] = useState("");

  //  دریافت سوالات
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await apiFetch({
        data: { params: [Number(form_id), null, null, null, 4, 4,null] },
      });
        console.log("RAW_FROM_API:", res);   // دیباگ
      if (res.success && Array.isArray(res.result)) {
          const formatted = res.result.map((item: any) => ({
              id: item.QID,
              title: item.Title,
              type: item.QType,
              sid: item.SID,
              options: (() => {
                  try {
                      return typeof item.Opt === "string" ? JSON.parse(item.Opt) : (item.Opt || []);
                  } catch {
                      return [];
                  }
              })(),
              isActive: item.Status?.data?.[0] === 1,
          }));
          console.log("FORMATTED_QUESTIONS:", formatted);
          setQuestions(formatted);
      } else if (res.success && Array.isArray(res.rows) && res.rows.length === 0) {
        setQuestions([]);
      } else {
        // اگر ساختار متفاوت بود، تلاش به خواندن rows
        if (res.success && Array.isArray(res.rows)) {
            const formatted = res.rows.map((item: any) => ({
                id: item.QID || item.id,
                title: item.Title || item.title,
                type: item.QType || item.type,
                sid: item.SID || null,
                options: item.Opt
                    ? JSON.parse(item.Opt)
                    : item.Options || [],
                isActive: item.Status?.data?.[0] === 1,
            }));

            setQuestions(formatted);
        } else {
          setError("پاسخ سرور نامعتبر است");
        }
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      setError(axiosError.message);
    } finally {
      setLoading(false);
    }
  };

  //  دریافت موضوعات (در صورت نیاز)
  const fetchSections = async () => {
    try {
      const res = await apiFetch({
        data: { params: [null, null, 4, 1, null] },
      });
    } catch (error) {
      console.error("خطا در دریافت موضوعات:", error);
    }
  };

  useEffect(() => {
    fetchQuestions();
    fetchSections();
  }, [form_id]);

  //  جستجو با تأخیر
  const handleSearch = (text: string) => {
    setSearchTerm(text);
    setSearchDone(false);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (text.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const res = await apiFetch({
          data: { params: [null, text, null, null, null, 4, 0, null] },
        });
        if (res.success && Array.isArray(res.rows)) {
          setSearchResults(res.rows);
        } else if (res.success && Array.isArray(res.result)) {
          setSearchResults(res.result);
        } else {
          setSearchResults([]);
        }
      } catch (e) {
        console.error("خطا در جستجو:", e);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
        setSearchDone(true);
      }
    }, 500);
  };

  // ➕ ثبت سؤال انتخاب‌شده (افزودن به فرم)
  const handleSubmitQuestion = async (QID: string | number) => {
    try {
      setLoading(true);
      const res = await apiFetch({
        data: {
          params: [Number(form_id), QID, 5, 1, 1, 4, null],
        },
      });

      if (res.success) {
        alert("سؤال با موفقیت ثبت شد ✅");
        setShowSearchModal(false);
        setSearchTerm("");
        setSearchResults([]);
        fetchQuestions();
      } else {
        alert("خطا در ثبت سؤال ❌");
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      alert("خطا در ارتباط با سرور: " + axiosError.message);
    } finally {
      setLoading(false);
    }
  };

  // غیرفعال کردن سؤال
  const handleDeactivate = async (qid: number) => {
    if (!window.confirm("آیا از غیرفعال کردن این سؤال مطمئن هستید؟")) return;

    try {
      setLoading(true);
      const res = await apiFetch({
        data: { params: [form_id, qid, 6, 0, 1, 4, null] },
      });

      if (res.success) {
        alert("سؤال غیرفعال شد ✅");
        fetchQuestions();
      } else {
        alert("خطا در غیرفعال کردن سؤال ❌");
      }
    } catch (e) {
      alert("خطا در ارتباط با سرور ❌");
    } finally {
      setLoading(false);
    }
  };

  //  دریافت شرط‌ها
  const fetchConditions = async (fromQID: number) =>
  {
    try {
      const res = await apiFetch({
        data: { params: [fromQID, null, null, null,null, Number(form_id), 2 ,5, null] },
      });
      if (res.success && Array.isArray(res.rows)) {
        setConditions(res.rows);
      } else if (res.success && Array.isArray(res.result)) {
        setConditions(res.result);
      } else {
        setConditions([]);
      }
    } catch (e) {
      console.error("خطا در دریافت شرط‌ها:", e);
      setConditions([]);
    }
  };

  // ❌ حذف شرط
  const handleDeleteCondition = async (flowID: number) => {
    if (!window.confirm("آیا از حذف این شرط مطمئن هستید؟")) return;

    try {
      const res = await apiFetch({
        data: { params: [null, flowID,null, null, null, null, 3, 5, null] },
      });
      if (res.success) {
        alert("شرط حذف شد ✅");
        fetchConditions(selectedQuestion.id);
      } else {
        alert("خطا در حذف شرط ❌");
      }
    } catch (e) {
      alert("خطا در ارتباط با سرور ❌");
    }
  };

  // باز کردن مودال شرط
  const handleOpenCondition = (q: any) => {
    setSelectedQuestion(q);
    setSelectedOption("");
    setSelectedOptionOID("");
    setTargetQuestion("");
    setShowConditionModal(true);
    fetchConditions(q.id);
  };

  // ثبت شرط
  const handleSubmitCondition = async () => {
    if (!selectedOption || !targetQuestion) {
      alert("لطفاً همه فیلدها را انتخاب کنید ❗");
      return;
    }

    try {
      setLoading(true);
      const res = await apiFetch({
        data: {
          params: [
            Number(selectedQuestion?.id),
            null,
            Number(selectedOptionOID || selectedOption),
            Number(targetQuestion),
            null,
            Number(form_id),
            1,
            5, null
          ],
        },
      });

      if (res.success) {
        alert("شرط با موفقیت ثبت شد ✅");
        setShowConditionModal(false);
        setSelectedOption("");
        setTargetQuestion("");
        fetchQuestions();
        fetchConditions(selectedQuestion.id);
      } else {
        alert("خطا در ثبت شرط ❌");
      }
    } catch (e) {
      alert("خطا در ارتباط با سرور ❌");
    } finally {
      setLoading(false);
    }
  };

  // ذخیره متن خوش‌آمد / پایان
  const handleSaveText = (type: "welcome" | "end") => {
    if (type === "welcome") {
      alert("متن خوش‌آمد ذخیره شد ✅");
      setShowWelcomeModal(false);
    } else {
      alert("متن پایان ذخیره شد ✅");
      setShowEndModal(false);
    }
  };

  return (
    <>
      <BreadCrumb  title="طراحی نظرسنجی" pageTitle="Survey Builder" />


<div  className=" flex justify-center  ">
    <div className="w-3/4 flex gap-4">
    <button
        onClick={() => navigate(`/form/result/${form_id}`)}
        className="flex items-center gap-2 bg-custom-500 text-white px-5 py-2 rounded-xl hover:bg-custom-600 transition-all shadow-sm"
    >
    نتایج
    </button>

    <button
        onClick={() => navigate(`/form/chart/${form_id}`)}
        className="flex items-center gap-2 bg-custom-500 text-white px-5 py-2 rounded-xl hover:bg-custom-600 transition-all shadow-sm"
    >
    نمودار ها
    </button>
    </div>
</div>
      <div className="flex flex-col gap-4 items-center mt-8">
        {/* صفحه خوش‌آمد */}
        <button
          onClick={() => setShowWelcomeModal(true)}
          className="w-3/4 py-3 border border-dashed border-slate-300 rounded-lg text-center text-slate-700 hover:bg-slate-50 dark:text-zink-200"
        >
          صفحه خوش‌آمدگویی +
        </button>

        {/* سوالات */}
        <div className="w-3/4 bg-slate-50 dark:bg-zink-800 border border-slate-200 dark:border-zink-600 rounded-lg py-8 text-center min-h-[200px] flex flex-col justify-center">
          {loading ? (
            <div className="flex justify-center items-center text-slate-600">
              <Loader2 className="animate-spin mr-2" />
              در حال بارگذاری سوالات...
            </div>
          ) : error ? (
            <div className="text-red-500">خطا در دریافت داده‌ها: {error}</div>
          ) : questions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-slate-500">
              <FileQuestion size={48} className="text-slate-400 mb-3" />
              <p className="mb-3">سؤالی برای نمایش وجود ندارد</p>
              <button
                onClick={() => setShowSearchModal(true)}
                className="flex items-center gap-2 bg-custom-500 text-white px-5 py-2 rounded-xl hover:bg-custom-600 transition-all shadow-sm"
              >
                <Plus size={18} /> افزودن سؤال
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {questions.map((q, i) => (
                <div
                  key={q.id}
                  className="flex justify-between items-center bg-white dark:bg-zink-700 border border-slate-200 dark:border-zink-600 rounded-lg px-4 py-2 shadow-sm"
                >
                  <div className="text-right">
                    <span className="font-medium">{i + 1}. {q.title}</span>
                    <div className="text-xs text-slate-500">
                      {q.type === 1 ? "چندگزینه‌ای" : "تشریحی"}{" "}
                      {!q.isActive && <span className="text-red-500 mx-2">(غیرفعال)</span>}
                    </div>
                  </div>

                  {/* دکمه‌ها */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenCondition(q)}
                      className="p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-md text-sm flex items-center gap-1"
                    >
                      <Settings2 size={16} /> شرط
                    </button>

                    <button
                      onClick={() => handleDeactivate(q.id)}
                      className="p-2 bg-red-100 text-red-600 hover:bg-red-200 rounded-md text-sm flex items-center gap-1"
                    >
                      <Ban size={16} /> غیرفعال
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => setShowSearchModal(true)}
                  className="flex items-center gap-2 bg-custom-500 text-white px-4 py-2 rounded-xl hover:bg-custom-600 transition-all shadow-sm"
                >
                  <Plus size={16} /> افزودن سؤال جدید
                </button>
              </div>
            </div>
          )}
        </div>

        {/* صفحه پایان */}
        <button
          onClick={() => setShowEndModal(true)}
          className="w-3/4 py-3 border border-dashed border-slate-300 rounded-lg text-center text-slate-700 hover:bg-slate-50 dark:text-zink-200"
        >
          صفحه پایان +
        </button>
      </div>

      {/* مدال جستجو و ثبت سؤال */}
      {showSearchModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white dark:bg-zink-800 rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-zink-100 flex items-center gap-2">
                <Search size={18} /> جستجوی سؤال
              </h3>
              <button onClick={() => setShowSearchModal(false)}>
                <X className="text-slate-600 hover:text-red-500" />
              </button>
            </div>

            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="form-input w-full border-slate-300 dark:bg-zink-700 dark:border-zink-600 mb-4"
              placeholder="حداقل ۳ کاراکتر برای جستجو..."
            />

            {searchLoading ? (
              <div className="flex items-center text-slate-500">
                <Loader2 className="animate-spin mr-2" /> در حال جستجو...
              </div>
            ) : searchResults.length > 0 ? (
              <ul className="border border-slate-200 rounded max-h-60 overflow-y-auto">
                {searchResults.map((q: any) => (
                  <li
                    key={q.QID || q.id}
                    onClick={() => handleSubmitQuestion(q.QID || q.id)}
                    className="cursor-pointer px-3 py-2 hover:bg-slate-100 text-sm"
                  >
                    {q.Title || q.title}
                  </li>
                ))}
              </ul>
            ) : (
              searchDone && (
                <p className="text-sm text-slate-500">سؤالی یافت نشد</p>
              )
            )}
          </div>
        </div>
      )}

      {/* Modal شرط */}
      {showConditionModal && selectedQuestion && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white dark:bg-zink-800 rounded-lg shadow-lg w-full max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-zink-100">
                ثبت شرط برای "{selectedQuestion.title}"
              </h3>
              <button onClick={() => setShowConditionModal(false)}>
                <X className="text-slate-600 hover:text-red-500" />
              </button>
            </div>

            {/* Dropdown گزینه‌ها */}
            <label className="block text-sm mb-2">گزینه سؤال:</label>
            <select
              className="w-full border rounded p-2 mb-4"
              value={selectedOption}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedOption(val);
                const found = selectedQuestion?.options?.find(
                  (o: any) => (o.Title ?? o.title ?? String(o.OID ?? o.id)) === val
                );
                setSelectedOptionOID(found?.OID ?? found?.id ?? "");
              }}
            >
              <option value="">انتخاب کنید...</option>
              {selectedQuestion.options?.length ? (
                selectedQuestion.options.map((opt: any, i: number) => (
                  <option key={i} value={opt.Title ?? opt.title}>
                    {opt.Title ?? opt.title}
                  </option>
                ))
              ) : (
                <option value="">(بدون گزینه)</option>
              )}
            </select>

            <p className="text-center my-2 text-sm">برابر بود با</p>

            {/* Dropdown سؤال مقصد */}
            <label className="block text-sm mb-2">سؤال مقصد:</label>
            <select
              className="w-full border rounded p-2 mb-4"
              value={targetQuestion}
              onChange={(e) => setTargetQuestion(e.target.value)}
            >
              <option value="">انتخاب کنید...</option>
              {questions
                .filter((q) => q.id !== selectedQuestion.id)
                .map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.title}
                  </option>
                ))}
            </select>

            <div className="flex justify-end gap-2 mb-6">
              <button
                onClick={() => setShowConditionModal(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                انصراف
              </button>
              <button
                onClick={handleSubmitCondition}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                ثبت شرط
              </button>
            </div>

            {/* لیست شرط‌ها */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-2 text-slate-700 dark:text-zink-200">
                شرط‌های ثبت‌شده:
              </h4>
              {conditions.length === 0 ? (
                <p className="text-sm text-slate-500 text-center">هیچ شرطی ثبت نشده است</p>
              ) : (
                <ul className="space-y-2 max-h-60 overflow-y-auto">
                  {conditions.map((c: any) => (
                    <li
                      key={c.FlowID}
                      className="flex justify-between items-center border rounded px-3 py-2 bg-slate-50 dark:bg-zink-700"
                    >
                      <div className="text-sm">
                        <span className="font-medium">{c.OptionTitle}</span>
                        <span className="mx-1 text-gray-500">→</span>
                        <span>{c.ToQuestionTitle}</span>
                      </div>
                      <button
                        onClick={() => handleDeleteCondition(c.FlowID)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* دیالوگ خوش‌آمدگویی */}
      {showWelcomeModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white dark:bg-zink-800 rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-zink-100">
                متن صفحه خوش‌آمدگویی
              </h3>
              <button onClick={() => setShowWelcomeModal(false)}>
                <X className="text-slate-600 hover:text-red-500" />
              </button>
            </div>

            <textarea
              value={welcomeText}
              onChange={(e) => setWelcomeText(e.target.value)}
              rows={4}
              className="w-full border border-slate-300 dark:bg-zink-700 dark:border-zink-600 rounded p-2"
              placeholder="متن خوش‌آمدگویی را وارد کنید..."
            />

            <div className="flex justify-end mt-4">
              <button
                onClick={() => handleSaveText("welcome")}
                className="btn bg-custom-500 text-white hover:bg-custom-600"
              >
                ذخیره
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🏁 دیالوگ پایان */}
      {showEndModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white dark:bg-zink-800 rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-zink-100">
                متن صفحه پایان
              </h3>
              <button onClick={() => setShowEndModal(false)}>
                <X className="text-slate-600 hover:text-red-500" />
              </button>
            </div>

            <textarea
              value={endText}
              onChange={(e) => setEndText(e.target.value)}
              rows={4}
              className="w-full border border-slate-300 dark:bg-zink-700 dark:border-zink-600 rounded p-2"
              placeholder="متن صفحه پایان را وارد کنید..."
            />

            <div className="flex justify-end mt-4">
              <button
                onClick={() => handleSaveText("end")}
                className="btn bg-custom-500 text-white hover:bg-custom-600"
              >
                ذخیره
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SurveyBuilder;

